// Accessibility fixes that keep the look unchanged (WCAG 2.2 AA findings, 2026-10).

// Carousels: links in slides that are hidden from assistive tech (aria-hidden="true") are taken out of the tab
// order and come back when the slide shows again. Slick marks off-screen slides that way, and so do the
// inspiration carousel and the review showcase.
(() => {
  const SLIDES = '.slick-slide, [data-inspiration-slide], [data-review-slide]';
  const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]';

  const sync = (slide) => {
    const hidden = slide.getAttribute('aria-hidden') === 'true';
    slide.querySelectorAll(FOCUSABLE).forEach((el) => {
      if (hidden) {
        if (!el.hasAttribute('data-a11y-tabindex')) el.setAttribute('data-a11y-tabindex', el.getAttribute('tabindex') ?? '');
        el.setAttribute('tabindex', '-1');
      } else if (el.hasAttribute('data-a11y-tabindex')) {
        const before = el.getAttribute('data-a11y-tabindex');
        if (before === '') el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', before);
        el.removeAttribute('data-a11y-tabindex');
      }
    });
  };

  // Slick names every carousel "region" without a label; give each one the name of what it shows
  const nameCarousels = () => {
    document.querySelectorAll('[aria-roledescription="carousel"]:not([aria-label]):not([aria-labelledby])').forEach((carousel) => {
      let name = 'Karussell';
      if (carousel.id === 'main-image') name = 'Produktbilder';
      else if (carousel.classList.contains('thumbnails-slider')) name = 'Vorschaubilder';
      else {
        const section = carousel.closest('section, .shopify-section');
        const heading = section && section.querySelector('h2, h3');
        if (heading && heading.textContent.trim()) name = heading.textContent.trim().replace(/\s+/g, ' ');
      }
      carousel.setAttribute('aria-label', name);
    });
  };

  // Links hidden from assistive tech on purpose (e.g. the image link next to the title link on product cards) stay
  // out of the tab order; Slick sets tabindex="0" on every link of the slide it shows
  const keepHiddenLinksOut = () => {
    document.querySelectorAll('a[aria-hidden="true"]:not([tabindex="-1"])').forEach((link) => link.setAttribute('tabindex', '-1'));
  };

  const run = () => {
    document.querySelectorAll(SLIDES).forEach(sync);
    keepHiddenLinksOut();
    nameCarousels();
  };

  // Cheap triggers only (watching every DOM change cost ~0.7 s of main thread on product pages): slides that are
  // shown or hidden, Slick's own events (it re-enables links in the slide it shows), and the page load
  new MutationObserver((records) => {
    for (const record of records) {
      if (record.target.matches(SLIDES)) sync(record.target);
    }
  }).observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['aria-hidden'] });

  let queued = false;
  const queueRun = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      run();
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    if (window.jQuery) window.jQuery(document).on('init reInit afterChange', '.slick-slider', queueRun);
  });
  window.addEventListener('load', () => {
    run();
    setTimeout(run, 3000); // app widgets (reviews, recommendations) that render after load
  });
  if (document.readyState !== 'loading') run();
})();

// Mobile menu: while it is closed it stays out of the tab order and away from screen readers (it only slides off
// screen); the theme toggles .active on #mobile-menu
(() => {
  const menu = document.querySelector('#mobile-menu');
  if (!menu) return;
  const sync = () => {
    const open = menu.classList.contains('active');
    if (open) menu.removeAttribute('inert');
    else menu.setAttribute('inert', '');
  };
  sync();
  new MutationObserver(sync).observe(menu, { attributes: true, attributeFilter: ['class'] });
})();

// Embedded frames get a title (WCAG 4.1.2): Shopify's tracking sandboxes are hidden from assistive tech, the chat,
// Trusted Shops and video frames are named. Apps insert them late, so look a few times after load.
(() => {
  const name = (frame) => {
    const src = (frame.src || '') + ' ' + (frame.id || '') + ' ' + (frame.name || '') + ' ' + (frame.className || '');
    if (/web-pixel|sandbox|detect_close|__uw|klaviyo-iframe-analytics/i.test(src)) {
      frame.setAttribute('aria-hidden', 'true');
      frame.setAttribute('tabindex', '-1');
      return 'Technischer Rahmen ohne Inhalt';
    }
    if (/gorgias/i.test(src)) return 'Chat mit dem SILBERTHAL-Kundenservice';
    if (/trustedshops|trustbadge/i.test(src)) return 'Trusted Shops Käuferschutz und Bewertungen';
    if (/youtube|vimeo/i.test(src)) return 'Video';
    if (/klaviyo/i.test(src)) return 'Newsletter-Anmeldung';
    if (/paypal/i.test(src)) return 'PayPal';
    if (/userway/i.test(src)) return 'Barrierefreiheit Menü';
    return 'Eingebetteter Inhalt';
  };
  const run = () => document.querySelectorAll('iframe:not([title])').forEach((frame) => frame.setAttribute('title', name(frame)));
  window.addEventListener('load', () => {
    run();
    let rounds = 0;
    const timer = setInterval(() => {
      run();
      if (++rounds >= 10) clearInterval(timer);
    }, 3000);
  });
})();

// Skip link first: apps (cookie banner, Trusted Shops) insert their elements at the very top of the page after it
// loaded, so the first Tab from the start of the page goes to the skip link
(() => {
  const skip = document.querySelector('.st-skip-link');
  if (!skip) return;
  let used = false;
  document.addEventListener('keydown', (event) => {
    if (used || event.key !== 'Tab' || event.shiftKey) return;
    used = true;
    const active = document.activeElement;
    if (!active || active === document.body || active === document.documentElement) {
      event.preventDefault();
      skip.focus();
    }
  });
})();
